import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileText, FileSpreadsheet, Image as ImageIcon, File as FileIcon, X, CheckCircle2 } from "lucide-react";

const iconFor = (name = "", type = "") => {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (["jpg", "jpeg", "png"].includes(ext) || (type || "").startsWith("image/")) return { Icon: ImageIcon, cls: "bg-violet-50 text-violet-600 border-violet-200" };
  if (["xls", "xlsx", "csv"].includes(ext)) return { Icon: FileSpreadsheet, cls: "bg-teal-50 text-teal-600 border-teal-200" };
  if (ext === "pdf") return { Icon: FileText, cls: "bg-slate-100 text-[#0B1220] border-slate-300" };
  if (["doc", "docx", "txt"].includes(ext)) return { Icon: FileText, cls: "bg-blue-50 text-blue-600 border-blue-200" };
  return { Icon: FileIcon, cls: "bg-slate-100 text-slate-500 border-slate-200" };
};

export const prettySize = (n) => {
  n = Number(n) || 0;
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};

/**
 * DocUploader — premium drag-drop uploader with per-file progress bars + type icons.
 * Uploads to /api/hub/files/upload; calls onChange with the full updated file list.
 */
export const DocUploader = ({ files = [], onChange, max = 10, suggestions = [], testId = "doc-uploader", endpoint = "/hub/files/upload", allowRemove = true }) => {
  const [dragOver, setDragOver] = useState(false);
  const [pending, setPending] = useState([]); // {key, name, size, pct}
  const inputRef = useRef(null);

  const onFiles = async (fileList) => {
    const items = Array.from(fileList || []);
    if (!items.length) return;
    if (files.length + items.length > max) { toast.error(`Max ${max} files`); return; }
    const jobs = items.map((f, i) => ({ key: `${Date.now()}-${i}`, file: f, name: f.name, size: f.size, pct: 0 }));
    setPending((p) => [...p, ...jobs.map(({ file, ...rest }) => rest)]);
    const done = [];
    for (const job of jobs) {
      if (job.file.size > 75 * 1024 * 1024) {
        toast.error(`${job.name} is ${(job.file.size / 1048576).toFixed(1)}MB — the limit is 75MB`);
        setPending((p) => p.filter((x) => x.key !== job.key));
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", job.file);
        const r = await api.post(endpoint, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (ev) => {
            const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 60;
            setPending((p) => p.map((x) => (x.key === job.key ? { ...x, pct } : x)));
          },
        });
        done.push(r.data);
        setPending((p) => p.filter((x) => x.key !== job.key));
      } catch (e) {
        toast.error(e?.response?.data?.detail || `${job.name} failed to upload`);
        setPending((p) => p.filter((x) => x.key !== job.key));
      }
    }
    if (done.length) {
      onChange([...files, ...done]);
      toast.success(`Uploaded ${done.length} file${done.length === 1 ? "" : "s"}`);
    }
  };

  const remove = (id) => onChange(files.filter((f) => f.id !== id));

  return (
    <div className="space-y-3">
      {/* Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); onFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`group relative overflow-hidden rounded-2xl border-2 border-dashed p-7 text-center cursor-pointer transition-all duration-300 ${
          dragOver
            ? "border-[#0055FF] bg-[#0055FF]/[0.04] scale-[1.01]"
            : "border-slate-200 bg-gradient-to-b from-slate-50 to-white hover:border-[#0055FF]/40"
        }`}
        data-testid={testId}
      >
        <div className={`mx-auto grid place-items-center size-12 rounded-2xl bg-gradient-to-br from-[#0055FF] to-[#003ECC] text-white shadow-lg shadow-[#0055FF]/25 transition-transform duration-300 ${dragOver ? "scale-110 -rotate-6" : "group-hover:scale-105"}`}>
          <UploadCloud className="size-5" />
        </div>
        <div className="mt-3 text-[13.5px] font-semibold text-slate-900">{dragOver ? "Drop to upload" : "Drag & drop, or click to browse"}</div>
        <div className="mt-1 text-[11.5px] text-slate-500">PDF · JPG · PNG · DOC · XLS · CSV · TXT — up to {max} files · 75MB each</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt,application/pdf,image/*"
          className="hidden"
          onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
          data-testid={`${testId}-input`}
        />
      </div>

      {/* Suggested docs */}
      {suggestions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-400 mr-1">Lenders love:</span>
          {suggestions.map((s) => (
            <span key={s} className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-2 h-6 text-[11px] font-medium text-slate-600">{s}</span>
          ))}
        </div>
      )}

      {/* In-flight uploads */}
      {pending.map((p) => {
        const { Icon, cls } = iconFor(p.name);
        return (
          <div key={p.key} className="flex items-center gap-3 rounded-xl border border-[#0055FF]/25 bg-[#0055FF]/[0.03] p-3" data-testid={`${testId}-uploading`}>
            <span className={`grid place-items-center size-9 rounded-lg border shrink-0 ${cls}`}><Icon className="size-4" /></span>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-slate-900 truncate">{p.name}</div>
              <div className="mt-1.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-[#0055FF] to-[#3b82f6] transition-all duration-200" style={{ width: `${p.pct}%` }} />
              </div>
            </div>
            <div className="text-[11px] font-mono text-[#0055FF] tabular-nums shrink-0">{p.pct}%</div>
          </div>
        );
      })}

      {/* Uploaded files */}
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((f) => {
            const { Icon, cls } = iconFor(f.filename, f.content_type);
            return (
              <div key={f.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 animate-in fade-in slide-in-from-bottom-1 duration-300" data-testid={`${testId}-file-${f.id}`}>
                <span className={`grid place-items-center size-9 rounded-lg border shrink-0 ${cls}`}><Icon className="size-4" /></span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-slate-900 truncate">{f.filename}</div>
                  <div className="text-[11px] text-slate-500">{prettySize(f.size)}</div>
                </div>
                <CheckCircle2 className="size-4 text-teal-500 shrink-0" />
                {allowRemove && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); remove(f.id); }} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors" data-testid={`${testId}-remove-${f.id}`}>
                    <X className="size-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
